import { t, Trans } from "@lingui/macro";
import { memo, useCallback, useContext } from "react";

import { languages } from "../locales/i18n";
import { Box } from "../slang";
import { Button2, Page, Section } from "../ui/Shared";
import { PageTitle, SectionTitle } from "../ui/Typography";
import { AppContext } from "./AppContextProvider";
import styles from "./Settings.module.css";

const Settings = memo(() => {
  const { updateUserSettings, mode, language } = useContext(AppContext);
  const setLightMode = useCallback(() => {
    document.body.classList.add("disableAnimation");
    updateUserSettings({ mode: "light" });
    setTimeout(() => document.body.classList.remove("disableAnimation"), 100);
  }, [updateUserSettings]);
  const setDarkMode = useCallback(() => {
    document.body.classList.add("disableAnimation");
    updateUserSettings({ mode: "dark" });
    setTimeout(() => document.body.classList.remove("disableAnimation"), 100);
  }, [updateUserSettings]);
  const changeLanguage = useCallback(
    (l: string) => {
      updateUserSettings({ language: l });
    },
    [updateUserSettings]
  );

  return (
    <Page>
      <PageTitle className="text-center w-full">
        <Trans>Settings</Trans>
      </PageTitle>
      <Section>
        <SectionTitle>
          <Trans>Language</Trans>
        </SectionTitle>
        <Box
          className={styles.ButtonGroupTwoLines}
          gap={1}
          at={{
            small: {
              template: "auto / repeat(2, minmax(0, 1fr))",
            },
            tablet: {
              items: "normal stretch",
              template: "auto / repeat(4, 150px)",
              gap: 1,
            },
          }}
        >
          {Object.keys(languages).map((locale) => (
            <GroupButton
              key={locale}
              disabled={language === locale}
              onClick={() => changeLanguage(locale)}
              aria-label={`Select Language: ${
                languages[locale as keyof typeof languages]
              }`}
            >
              {languages[locale as keyof typeof languages]}
            </GroupButton>
          ))}
        </Box>
      </Section>
      <Section>
        <SectionTitle>
          <Trans>Appearance</Trans>
        </SectionTitle>
        <Box
          flow="column"
          className={styles.ButtonGroupTwoLines}
          gap={1}
          at={{
            tablet: {
              items: "normal stretch",
              template: "auto / repeat(4, 150px)",
              gap: 1,
            },
          }}
        >
          <GroupButton
            disabled={mode === "light"}
            aria-pressed={mode === "light"}
            aria-label={t`Light Mode`}
            onClick={setLightMode}
          >
            <Trans>Light Mode</Trans>
          </GroupButton>
          <GroupButton
            disabled={mode === "dark"}
            aria-pressed={mode === "dark"}
            aria-label={t`Dark Mode`}
            onClick={setDarkMode}
          >
            <Trans>Dark Mode</Trans>
          </GroupButton>
        </Box>
      </Section>
      <Section className={styles.LowerLinks}>
        <SectionTitle>
          <Trans>About</Trans>
        </SectionTitle>
        <p className="text-sm md:leading-normal">
          <Trans>
            <span>TDA Diagram Builder</span> is a powerful diagramming service
            that supports many popular diagram libraries, making it well-suited
            for TDA Corporation to build and share diagrams across projects.
          </Trans>
        </p>
        <Section>
          <a
            href="https://tda.company"
            className="text-xs opacity-60 hover:opacity-100"
          >
            Homepage
          </a>
          <a
            href="https://github.com/tda-corp"
            className="text-xs opacity-60 hover:opacity-100"
          >
            <Trans>View on Github</Trans>
          </a>
        </Section>
      </Section>
      <Section className={styles.LowerLinks}>
        <SectionTitle>
          <Trans>Thankful</Trans>
        </SectionTitle>
        <p className="text-sm md:leading-normal">
          <Trans>
            Thanks to Tone Row (Flowchart Fun) for their great open-source work,
            which we used as a baseline to build this service.
          </Trans>
        </p>
        <Section>
          <a
            href="https://tone-row.com/"
            className="text-xs opacity-60 hover:opacity-100"
          >
            Homepage
          </a>
          <a
            href="https://flowchart.fun/"
            className="text-xs opacity-60 hover:opacity-100"
          >
            Flowchart Fun
          </a>
        </Section>
      </Section>
    </Page>
  );
});

Settings.displayName = "Settings";

export default Settings;

const GroupButton = memo(
  ({ children, className = "", ...props }: Parameters<typeof Button2>[0]) => {
    return (
      <Button2 className={[styles.GroupButton, className].join(" ")} {...props}>
        {children}
      </Button2>
    );
  }
);

GroupButton.displayName = "GroupButton";
